import { create } from 'zustand';
import {
  orgSkillRegistryApi,
  type RegisteredSkill,
  type SkillVersionRecord,
} from '@/services/api';

// Default org for the admin demo context.
// In a multi-tenant deployment this would come from auth context.
export const DEFAULT_ORG = 'neurograph-global';

interface SkillRegistryState {
  orgUid: string;
  skills: RegisteredSkill[];
  selectedSkill: RegisteredSkill | null;
  versions: SkillVersionRecord[];
  selectedVersion: SkillVersionRecord | null;
  loading: boolean;
  versionsLoading: boolean;
  error: string | null;

  setOrgUid: (orgUid: string) => void;
  fetchSkills: (status?: string) => Promise<void>;
  selectSkill: (skill: RegisteredSkill | null) => void;
  fetchVersions: (skillId: string) => Promise<void>;
  selectVersion: (version: SkillVersionRecord | null) => void;
}

export const useSkillRegistryStore = create<SkillRegistryState>((set, get) => ({
  orgUid: DEFAULT_ORG,
  skills: [],
  selectedSkill: null,
  versions: [],
  selectedVersion: null,
  loading: false,
  versionsLoading: false,
  error: null,

  setOrgUid: (orgUid) => set({ orgUid }),

  fetchSkills: async (status?: string) => {
    const { orgUid } = get();
    set({ loading: true, error: null });
    try {
      const res = await orgSkillRegistryApi.listSkills(orgUid, status);
      set({ skills: res.skills });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load skills' });
    } finally {
      set({ loading: false });
    }
  },

  selectSkill: (skill) => set({ selectedSkill: skill, versions: [], selectedVersion: null }),

  fetchVersions: async (skillId: string) => {
    const { orgUid } = get();
    set({ versionsLoading: true, error: null });
    try {
      const res = await orgSkillRegistryApi.listVersions(orgUid, skillId);
      set({ versions: res.versions });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load versions' });
    } finally {
      set({ versionsLoading: false });
    }
  },

  selectVersion: (version) => set({ selectedVersion: version }),
}));
